import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { type DrillDownResult } from '@apm/contracts';
import { Button } from '../ui/button';
import { Skeleton } from '../ui/states';
import { displayDate } from '../../lib/date';

/**
 * Danh sách task đứng sau một ô của biểu đồ — spec 16.6, tính chất bắt buộc số 2.
 *
 * Dùng chung cho **mọi** chart: click vào cột nào, vùng nào, chấm nào cũng mở
 * ra đúng bảng này. Mỗi chart tự dựng bảng riêng là mỗi chart hiện một tập cột
 * khác nhau, và người dùng phải học lại từ đầu ở mỗi màn hình.
 */
export function DrillDownPanel({
  result,
  loading = false,
  onClose,
  error = null,
  onRetry,
}: {
  result: DrillDownResult | null;
  loading?: boolean;
  onClose: () => void;
  error?: string | null;
  onRetry?: () => void;
}): ReactNode {
  const { t } = useTranslation();

  if (!loading && !result && !error) return null;

  return (
    <div className="mt-2 rounded-DEFAULT border border-border">
      <header className="flex flex-wrap items-center gap-2 border-b border-border px-2 py-1.5">
        <span className="text-xs font-medium">
          {result
            ? t(`chart.drillDown.${result.metric}`, {
                date: result.bucket,
                category: result.category ? t(`chart.category.${result.category}`) : '',
              })
            : t('common.loading')}
        </span>
        {result && (
          <span className="text-xs text-muted-foreground">
            {t('chart.itemCount', { count: result.totalCount })}
          </span>
        )}

        <Button
          size="icon"
          variant="ghost"
          className="ml-auto size-7"
          aria-label={t('chart.closeDrillDown')}
          onClick={onClose}
        >
          <X />
        </Button>
      </header>

      {error ? (
        <div role="alert" className="p-2 text-xs text-rag-red">
          {error}
          {onRetry && (
            <Button size="sm" variant="ghost" onClick={onRetry}>
              {t('common.retry')}
            </Button>
          )}
        </div>
      ) : loading ? (
        <Skeleton className="m-2 h-20" />
      ) : result!.items.length === 0 ? (
        <p className="px-2 py-3 text-xs text-muted-foreground">{t('chart.drillDownEmpty')}</p>
      ) : (
        <ul className="divide-y divide-border">
          {result!.items.map((item) => (
            <li key={item.taskId} className="flex flex-wrap items-center gap-2 px-2 py-1.5 text-xs">
              <span className="font-mono">{item.taskKey}</span>
              <span className="min-w-40 flex-1">{item.title}</span>
              {item.deleted && (
                // Task đã xoá vẫn nằm trong số liệu của những ngày nó từng tồn
                // tại — nói ra, đừng để người đọc tưởng danh sách sai.
                <span className="text-rag-red">{t('chart.deletedTask')}</span>
              )}
              <span className="text-muted-foreground">{item.statusName ?? '—'}</span>
              <span className="text-muted-foreground">{item.assigneeName ?? '—'}</span>
              {item.dueDate && (
                <span className="text-muted-foreground">{displayDate(item.dueDate)}</span>
              )}
            </li>
          ))}
        </ul>
      )}

      {result && result.totalCount > result.items.length && (
        <p className="px-2 py-1.5 text-xs text-muted-foreground">
          {t('chart.drillDownTruncated', { count: result.totalCount - result.items.length })}
        </p>
      )}
    </div>
  );
}
